
import { mplBubblegum } from "@metaplex-foundation/mpl-bubblegum";
import { mplCore } from "@metaplex-foundation/mpl-core";
import { mplTokenMetadata } from "@metaplex-foundation/mpl-token-metadata";
import { mplToolbox } from "@metaplex-foundation/mpl-toolbox";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";
import { useTheme } from "@mui/material/styles";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Transaction } from "@solana/web3.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { TypeOptions, toast } from "react-toastify";
import {
  checkPriceAvailable,
  generatePreview,
  purchaseTraitByApi,
} from "../../lib/api";
import {
  BundleType,
  CollectionType,
  NftDataType,
  PriceType
} from "../../lib/types";
import { createPurchaseTx } from "../../lib/util";
import TraitSelectionPanel from "./TraitSelectionPanel";

interface Props {
  prices: PriceType[];
  selectedTraitType: string;
  AllFilteredNFTs: NftDataType[];
  setCollectionSelected: (collection: CollectionType | undefined) => void;
  selectedTraitTypeChange: (value: string) => void;
  setPageLoading: (_loading: boolean) => void;
  fetchWalletNfts: () => void;
  nftLoading: boolean;
  selectedCollection: CollectionType;
  getPrices: () => void;
  carouselRef: any;
  traitTypes: string[];
  selectedNft?: NftDataType | undefined;
  setSelectedNft?: (nft: NftDataType | undefined) => void;
}

const showToast = (message: string) => {
  const id = toast((t) => (
    <div className="flex items-center gap-3">
      <span className="text-2xl"></span>
      <p className="text-sm text-black">{message}</p>
    </div>
  ));

  setTimeout(() => toast.dismiss(id), 3000);
};

const ContentPanel = ({
  prices,
  selectedTraitType,
  AllFilteredNFTs,
  selectedTraitTypeChange,
  setCollectionSelected,
  selectedCollection,
  traitTypes,
  nftLoading,
  setPageLoading,
  fetchWalletNfts,
  getPrices,
  selectedNft,
  setSelectedNft,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [selectedTraitIndex, setSelectedTraitIndex] = useState<number | null>(null);

  const [selectedURL, setSelectedURL] = useState<string | undefined>(undefined);
  const [selectedCNFT, setSelectedCNFT] = useState<NftDataType | undefined>(
    undefined
  );
  const [selectedBundlePurchase, setSelectedBundlePurchase] = useState<
    BundleType | undefined
  >(undefined);
  const [selectedPurchase, setSelectedPurchase] = useState<
    PriceType | undefined
  >(undefined);
  const wallet = useWallet();
  const { connection } = useConnection();
  const theme = useTheme();

  const handleSelectBooby = (index: number) => {
    setSelectedTraitIndex(index);
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setSelectedTraitIndex(null);
  };

  const selectNftHandle = (nft: NftDataType) => {
    setSelectedCNFT(nft);
    handleDialogClose();
  };

  const umi = useMemo(
    () =>
      createUmi(
        "https://mainnet.helius-rpc.com/?api-key=c15e7b6d-b401-4e4b-af33-4155d22f2219"
      )
        .use(mplBubblegum())
        .use(mplToolbox())
        .use(mplCore())
        .use(walletAdapterIdentity(wallet, true))
        .use(mplTokenMetadata()),
    [wallet]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (selectedBundlePurchase != undefined && (selectedNft || selectedCNFT) != undefined) {
          const response = await generatePreview(
            (selectedNft || selectedCNFT)!.mint,
            "bundle",
            selectedBundlePurchase.id.toString()
          );
          setSelectedURL(response);
        } else if (selectedPurchase != undefined && (selectedNft || selectedCNFT) != undefined) {
          const response = await generatePreview(
            (selectedNft || selectedCNFT)!.mint,
            "trait",
            selectedPurchase.id.toString()
          );
          setSelectedURL(response);
        }
      } catch (error) {
        console.error("Error fetching preview:", error);
      }
    };

    fetchData();
  }, [selectedBundlePurchase, selectedPurchase]);

  const purchaseTrait = async () => {
    if (!selectedPurchase) {
      showToast("Please select a Trait first");
      return;
    }
    if (!wallet || !wallet.connected || !wallet.publicKey) {
      showToast("Please connect wallet first");
      return;
    }
    if (!(selectedNft || selectedCNFT)) {
      showToast("Please select a Boobie first");
      return;
    }

    let initialTxn: string = "";
    let itemType: string = "";
    let itemId: number = 0;
    let walletAddress: string = "";
    let transactionTimestamp: number = 0;
    try {
      showToast("Buying Trait..");
      setDisabled(true);
      setTimeout(() => {
        setDisabled(false); // Re-enable the button after 35 seconds
      }, 35000);

      setPageLoading(true);
      let result = await checkPriceAvailable(selectedPurchase.id);

      if (!result.result) {
        showToast(String(result.data),);
        setPageLoading(false);
        return;
      }
      result = await createPurchaseTx(
        selectedPurchase,
        umi,
        wallet,
        connection
      );

      if (!result.result) {
        showToast(String(result.data),);
        setPageLoading(false);
        return;
      }
      let tx = result.data as Transaction;

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
      tx.feePayer = wallet.publicKey;
      tx.recentBlockhash = blockhash;
      tx.lastValidBlockHeight = lastValidBlockHeight;

      const txId = await wallet.sendTransaction(tx, connection, {
        skipPreflight: true,
        maxRetries: 5,
      });

      const _transactionTimestamp = Math.floor(Date.now() / 1000);

      initialTxn = txId;
      itemType = "trait";
      itemId = selectedPurchase.id;
      walletAddress = wallet.publicKey.toString();
      transactionTimestamp = _transactionTimestamp;

      showToast("Updating Boobie ✨");
      const status = await purchaseTraitByApi(
        wallet.publicKey.toString(),
        selectedPurchase,
        txId,
        (selectedNft || selectedCNFT)!.mint,
        () => setPageLoading(false),
        (msg: string, type: TypeOptions) => showToast(msg),
        getPrices
      );

      fetchWalletNfts();
      showToast("Congratulations! Your Boobie is styling a new look! ✨");
      setPageLoading(false);

    } catch (e) {
      let error = (e as Error).toString();
      if (error.split(":")[1].split(".")[0].length > 0) {
        console.log("> ", error.split(":")[1].split(".")[0].length > 0);
        showToast(error.split(":")[1].split(".")[0]);
      }
      console.log(error);
      console.log(JSON.stringify(e as any));
      setPageLoading(false);
    }
  };

  const buttons = [
    { label: "Buy Trait", onClick: purchaseTrait, color: "error" },
  ];

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-4 lg:p-8 relative justify-center items-start pt-[120px]">
      {/* Control Panel */}
      <div className="glass-panel w-full lg:flex-1 p-6 rounded-2xl border border-white/10">
        <div className="flex flex-col gap-6">
          {/* Trait Marketplace Section */}
          <TraitSelectionPanel
            selectedTraitType={selectedTraitType}
            prices={prices}
            selectedCollection={selectedCollection}
            selectedPurchase={selectedPurchase}
            setSelectedPurchase={setSelectedPurchase}
            buttons={buttons}
            disabled={disabled}
            AllFilteredNFTs={AllFilteredNFTs}
            nftLoading={nftLoading}
            onSelectBooby={handleSelectBooby}
            getPrices={getPrices}
            fetchWalletNfts={fetchWalletNfts}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentPanel;

